
import { useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { Progress } from "@shared/components/ui/progress";
import BookingFooter from "@features/booking/components/BookingFooter";

interface BookingProgressFooterProps {
  onBack: () => void;
  progress?: number;
  showBack?: boolean;
}

const BookingProgressFooter = ({ onBack, progress = 70, showBack = true }: BookingProgressFooterProps) => {
  // Hold the last positive progress value. `useBookingFlow.progress` briefly
  // returns 0 in transitional moments — specifically when the user clicks
  // commit on a payment-host step, `flushSync(cardToken)` filters GUARANTEE
  // out of `activeSteps`, `currentStepIndex` drops to -1, progress → 0 —
  // for the few hundred ms between the commit POST and the navigation to
  // /booking-final. Showing a freshly-empty progress bar mid-commit reads
  // as a regression. Lock to the last-known good value during that window.
  const lastProgressRef = useRef(progress);
  if (progress > 0) lastProgressRef.current = progress;
  const displayProgress = progress > 0 ? progress : lastProgressRef.current;

  return (
  // Sticky to the viewport bottom so a tall page (payment forms, with card
  // + billing + fees + acceptance toggles + CTA) doesn't push the progress
  // bar below the fold. Opaque themes keep bg-card (clean surface behind
  // the progress bar); translucent themes swap to backdrop-blur so the
  // footer doesn't stack opacity on top of the chrome's translucent card.
  <footer className="border-t border-border bg-card lg:translucent:bg-transparent max-lg:translucent:backdrop-blur-sm sticky bottom-0 z-20">
    <div className="flex items-center px-6 py-4">
      {showBack && (
        <button
          aria-label="Go back to previous page"
          className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm mr-4"
          onClick={onBack}
          type="button"
        >
          <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
        </button>
      )}
      <Progress value={displayProgress} className="h-1.5 bg-foreground/10 flex-1" aria-label="Booking progress" />
    </div>
    <BookingFooter />
  </footer>
  );
};

export default BookingProgressFooter;
